import React, { useEffect, useState } from "react";
import { IoIosArrowBack } from "react-icons/io";
import HeadToHeadInfoCard from "../../components/headToHeadInfoCard/headToHeadInfoCard";
import HeadToHeadResult from "../../components/headToHeadResult/headToHeadResult";
import HeadToHeadPlayCard from "../../components/headToHeadPlayCard/headToHeadPlayCard";
import useHeadToHead from "../../services/hooks/useHeadToHead";
import Loader from "../../components/common/loader/loader";
import duration, { Duration } from "dayjs/plugin/duration";
import Ellipse from "../../assets/images/Ellipse.svg";
import Vs from "./../../assets/images/VS.svg";
import { convertTimeLeftInUnits, getFormattedDate } from "../../utils/common-function";
import dayjs from "dayjs";
import { h2hTimerAlertInHours } from "../../utils/constants";
import clsx from "clsx";
import { useHistory } from "react-router";
import CountDownTimer from "../../components/common/countDownTimer/countDownTimer";
import useDeviceSizes from "../../services/hooks/useDeviceSizes";
dayjs.extend(duration);

const HeadToHead: React.FC = () => {
  const history = useHistory();
  const { isMediumDevice } = useDeviceSizes();
  const { headToHeadLoading, headToHeadData, battleStatus } = useHeadToHead();

  const { player1Stashlink, player2Stashlink, endTime, goal, player1Score, player2Score } = headToHeadData ?? {};

  const completed = (player1Score ?? 0) + (player2Score ?? 0) >= (goal ?? 0);
  const inProgress = battleStatus === "currentHeadToHeadBattle" && !completed;

  const { days, hours, minutes, seconds } = getFormattedDate(endTime);

  const [timeLeft, setTimeLeft] = useState<Duration>(
    dayjs.duration({
      days: days,
      hours: hours,
      minutes: minutes,
      seconds: seconds
    })
  );

  useEffect(() => {
    const { days, hours, minutes, seconds } = getFormattedDate(endTime);
    setTimeLeft(dayjs.duration({ days: days, hours: hours, minutes: minutes, seconds: seconds }));
  }, [endTime]);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime.asSeconds() <= 1) {
          clearInterval(timer);
          return dayjs.duration(0);
        }
        return prevTime.subtract(1, "second");
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const isTimeAlmostOver = timeLeft.asSeconds() > 0 && convertTimeLeftInUnits(timeLeft) <= h2hTimerAlertInHours;

  if (headToHeadLoading) {
    return (
      <div className="h-[calc(100vh-80px)] w-screen flex justify-center items-center bg-soft-black">
        <Loader />
      </div>
    );
  }

  return (
    <div
      className={clsx([
        "min-h-screen text-white flex flex-col items-center",
        isMediumDevice ? "bg-black pattern-bg bg-cover bg-no-repeat pt-10" : "bg-soft-black"
      ])}>
      <div className="bg-soft-black flex flex-col items-center relative w-full max-w-md">
        <div className="md:relative fixed z-10 max-w-md w-full">
          <div
            className={clsx(
              "flex w-full max-w-md justify-center shadow-md md:rounded-t-lg",
              battleStatus === "currentHeadToHeadBattle" && !completed ? "bg-primary-l3" : "bg-white/10"
            )}>
            <div className="w-full px-2 gap-8 text-white rounded-lg">
              <div className="w-full h-12 flex items-center justify-between">
                {history.length > 2 ? (
                  <IoIosArrowBack onClick={() => history.goBack()} className="text-base-white cursor-pointer text-2xl h-8 w-8" />
                ) : (
                  <div className="w-8 h-8" />
                )}
                <h3 className="absolute left-1/2 -translate-x-1/2 font-secondary tracking-[0.064px] text-base text-center text-base-white font-extrabold">
                  HEAD TO HEAD
                </h3>
                <div className="relative flex flex-row gap-x-2">
                  <div
                    className={clsx(
                      "py-[6px] px-[10px] rounded-lg text-base-white bg-black/20 text-sm font-secondary font-bold flex flex-row items-center gap-x-2"
                    )}>
                    <span className="font-bold text-sm">{inProgress ? "LIVE" : "ENDED"}</span>
                    {inProgress ? <img src={Ellipse} alt="ellipse" className="w-[6px] h-[6px] animate-pulse" /> : null}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="mb-[28px] w-full">
            <CountDownTimer isTimeAlmostOver={isTimeAlmostOver} timeLeft={timeLeft} />
          </div>
        </div>
        <div className="md:h-auto h-32" />

        {inProgress ? (
          <HeadToHeadPlayCard
            goal={goal}
            player1Score={player1Score}
            player2Score={player2Score}
            isTimeAlmostOver={isTimeAlmostOver}
            player1Stashlink={player1Stashlink}
            player2Stashlink={player2Stashlink}
            timeLeft={timeLeft}
          />
        ) : (
          <HeadToHeadResult player1Stashlink={player1Stashlink} player2Stashlink={player2Stashlink} />
        )}
      </div>
      <div className="pb-40 md:bg-soft-black">
        <HeadToHeadInfoCard />
      </div>

      <div className="w-full bg-black px-4 pb-4 pt-[10px] fixed bottom-0">
        <div className="max-w-md w-full mx-auto">
          <div className="font-bold text-base-white font-secondary text-lg text-center">Choose a Team</div>

          <div className="flex flex-row justify-between items-center mt-[8px] pb-4">
            <button
              className="relative flex justify-start items-center rounded-[134px] bg-primary-l1 border-2 border-black shadow-[0px_4px_0px_#6C801F] text-black w-full h-12 px-2 py-2.5 text-base font-primary tracking-wider font-wght-750 leading-tight-19.2 gap-2 box-border select-none"
              onClick={() => history.push(player1Stashlink?.url ?? "")}>
              <img
                src={player1Stashlink?.user?.profilePhoto}
                alt={player1Stashlink?.user?.fullName}
                className="object-cover w-9 h-9 rounded-full border border-1 border-black rounded-tr-[136px] rounded-br-[136px]"
              />
              <div className="lining-nums ml-4 proportional-nums absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 text-black font-primary font-extrabold text-base">
                Play
              </div>
            </button>
            <img src={Vs} alt="vs" className="mx-2 w-6 h-6" />
            <button
              className="flex justify-end items-center relative rounded-[134px] bg-primary-l1 border-2 border-black shadow-[0px_4px_0px_#6C801F] text-black w-full h-12 px-2 py-2.5 text-base font-primary tracking-wider font-wght-750 leading-tight-19.2 gap-2 box-border select-none"
              onClick={() => history.push(`/stashlink/${player2Stashlink?.id}`)}>
              <img
                src={player2Stashlink?.user?.profilePhoto}
                alt={player2Stashlink?.user?.fullName}
                className="object-cover w-9 h-9 rounded-full border border-1 border-black rounded-tl-[136px] rounded-bl-[136px]"
              />
              <div className="lining-nums -ml-4 proportional-nums absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 text-black font-primary font-extrabold text-base">
                Play
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeadToHead;
